import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient, setSessionExpiredHandler } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/lib/theme-provider";
import { LanguageProvider } from "@/lib/i18n/language-context";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { SessionProvider, useSession } from "@/lib/session-context";
import { WarehouseFilterProvider } from "@/lib/warehouse-filter-context";
import { ErrorBoundary } from "@/components/error-boundary";
import { UpdateBanner } from "@/components/update-banner";
import { initGlobalErrorHandlers } from "@/lib/error-logger";
import { loadRouteChunk } from "@/lib/stale-bundle";
import { getRoleLevel } from "@shared/schema";
import { Loader2 } from "lucide-react";
import { lazy, Suspense, useEffect, type ComponentType } from "react";

function lazyRoute<T extends ComponentType<any>>(loader: () => Promise<{ default: T }>) {
  return lazy(() => loadRouteChunk(loader));
}

const AdminLayout = lazyRoute(() => import("@/components/admin-layout"));
const Dashboard = lazyRoute(() => import("@/pages/dashboard"));
const OrdersList = lazyRoute(() => import("@/pages/orders-list"));
const OrderDetail = lazyRoute(() => import("@/pages/order-detail"));
const OrderForm = lazyRoute(() => import("@/pages/order-form"));
const WarehousePacking = lazyRoute(() => import("@/pages/warehouse-packing"));
const WarehouseReceiving = lazyRoute(() => import("@/pages/warehouse-receiving"));
const WarehouseReturnTracking = lazyRoute(() => import("@/pages/warehouse-return-tracking"));
const WarehouseInspection = lazyRoute(() => import("@/pages/warehouse-inspection"));
const ReceivingStatistics = lazyRoute(() => import("@/pages/receiving-statistics"));
const TransportShipping = lazyRoute(() => import("@/pages/transport-shipping"));
const AuditLogPage = lazyRoute(() => import("@/pages/audit-log"));
const CustomerPortal = lazyRoute(() => import("@/pages/customer-portal"));
const CustomersPage = lazyRoute(() => import("@/pages/customers"));
const ProductsPage = lazyRoute(() => import("@/pages/products"));
const ServicesPage = lazyRoute(() => import("@/pages/services"));
const QuotesPage = lazyRoute(() => import("@/pages/quotes"));
const QuoteFormPage = lazyRoute(() => import("@/pages/quote-form"));
const QuoteDetailPage = lazyRoute(() => import("@/pages/quote-detail"));
const ContractsPage = lazyRoute(() => import("@/pages/contracts"));
const ContractDetailPage = lazyRoute(() => import("@/pages/contract-detail"));
const ContractTermsPage = lazyRoute(() => import("@/pages/contract-terms"));
const PublicContractSignPage = lazyRoute(() => import("@/pages/public-contract-sign"));
const PublicRegisterPage = lazyRoute(() => import("@/pages/public-register"));
const SelfRegisterPage = lazyRoute(() => import("@/pages/self-register"));
const PublicQuoteConfirmPage = lazyRoute(() => import("@/pages/public-quote-confirm"));
const PublicCustomerUpdatePage = lazyRoute(() => import("@/pages/public-customer-update"));
const LoginPage = lazyRoute(() => import("@/pages/login"));
const SettingsPage = lazyRoute(() => import("@/pages/settings"));
const AdminUsersPage = lazyRoute(() => import("@/pages/admin-users"));
const AdminNotificationsPage = lazyRoute(() => import("@/pages/admin-notifications"));
const AdminWarehousesPage = lazyRoute(() => import("@/pages/admin-warehouses"));
const FileArchivePage = lazyRoute(() => import("@/pages/file-archive"));
const AdminInventoryPage = lazyRoute(() => import("@/pages/admin-inventory"));
const UserStatisticsPage = lazyRoute(() => import("@/pages/user-statistics"));
const MyStatisticsPage = lazyRoute(() => import("@/pages/my-statistics"));
const MyPackedOrdersPage = lazyRoute(() => import("@/pages/my-packed-orders"));
const WorkLogPage = lazyRoute(() => import("@/pages/work-log"));
const StockStatusPage = lazyRoute(() => import("@/pages/stock-status"));
const AvailabilityCalendarPage = lazyRoute(() => import("@/pages/availability-calendar"));
const InventoryCountsPage = lazyRoute(() => import("@/pages/inventory-counts"));
const DiscrepancyLogPage = lazyRoute(() => import("@/pages/discrepancy-log"));
const IssuesPage = lazyRoute(() => import("@/pages/issues"));
const ShippingOptionsPage = lazyRoute(() => import("@/pages/shipping-options"));
const ErrorLogsPage = lazyRoute(() => import("@/pages/error-logs"));
const AdminEmailInboxPage = lazyRoute(() => import("@/pages/admin-email-inbox"));
const AdminSignatureCapturePage = lazyRoute(() => import("@/pages/admin-signature-capture"));
const StandardPackagesPage = lazyRoute(() => import("@/pages/standard-packages"));
const PreparationPage = lazyRoute(() => import("@/pages/preparation"));
const ReadyForInvoicingPage = lazyRoute(() => import("@/pages/ready-for-invoicing"));
const VippsLinksPage = lazyRoute(() => import("@/pages/vipps-links"));
const ShippingBookingPage = lazyRoute(() => import("@/pages/shipping-booking"));
const InquiriesPage = lazyRoute(() => import("@/pages/inquiries"));
const UkeplanPage = lazyRoute(() => import("@/pages/ukeplan"));
const NokkeltallPage = lazyRoute(() => import("@/pages/nokkeltall"));
const OkonomiFakturaerPage = lazyRoute(() => import("@/pages/okonomi-fakturaer"));
const OkonomiKvitteringerPage = lazyRoute(() => import("@/pages/okonomi-kvitteringer"));
const AutomationStatusPage = lazyRoute(() => import("@/pages/automation-status"));
const DriftPage = lazyRoute(() => import("@/pages/drift"));
const OnboardingLager = lazyRoute(() => import("@/pages/onboarding-lager"));
const PlaybackDevicesPage = lazyRoute(() => import("@/pages/playback-devices"));
const LoansPage = lazyRoute(() => import("@/pages/loans"));
const NotFound = lazyRoute(() => import("@/pages/not-found"));

function RouteLoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function ProtectedRoute({ children, requiredRole }: { children: React.ReactNode; requiredRole?: string }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [location] = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  // Check role hierarchy instead of exact match
  if (requiredRole && user?.role) {
    const userLevel = getRoleLevel(user.role);
    const requiredLevel = getRoleLevel(requiredRole);
    if (userLevel < requiredLevel) {
      return <Redirect to="/" />;
    }
  } else if (requiredRole && !user?.role) {
    return <Redirect to="/" />;
  }

  return <>{children}</>;
}

function Router() {
  return (
    <Switch>
      {/* Public routes - No sidebar, no auth */}
      <Route path="/login" component={LoginPage} />
      <Route path="/portal/:accessCode" component={CustomerPortal} />
      <Route path="/registrer/:token" component={PublicRegisterPage} />
      <Route path="/bli-kunde" component={SelfRegisterPage} />
      <Route path="/bekreft-tilbud/:token" component={PublicQuoteConfirmPage} />
      <Route path="/oppdater-kunde/:token" component={PublicCustomerUpdatePage} />
      <Route path="/signer-kontrakt/:id" component={PublicContractSignPage} />
      
      {/* Protected admin routes - With sidebar */}
      <Route path="/">
        <ProtectedRoute>
          <AdminLayout>
            <Dashboard />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/orders">
        <ProtectedRoute>
          <AdminLayout>
            <OrdersList />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/orders/new">
        <ProtectedRoute>
          <AdminLayout>
            <OrderForm />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/orders/:id">
        <ProtectedRoute>
          <AdminLayout>
            <OrderDetail />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/orders/:id/edit">
        <ProtectedRoute>
          <AdminLayout>
            <OrderForm />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/ready-for-invoicing">
        <ProtectedRoute>
          <AdminLayout>
            <ReadyForInvoicingPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/vipps-lenker">
        <ProtectedRoute>
          <AdminLayout>
            <VippsLinksPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/shipping-booking">
        <ProtectedRoute>
          <AdminLayout>
            <ShippingBookingPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/ukeplan">
        <ProtectedRoute>
          <AdminLayout>
            <UkeplanPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      {/* Opplaering (lager-rolle level 2 og opp, ikke sjaafoer) */}
      <Route path="/opplaering">
        <ProtectedRoute requiredRole="warehouse">
          <AdminLayout>
            <OnboardingLager />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/issues">
        <ProtectedRoute>
          <AdminLayout>
            <IssuesPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      {/* Morgen-digestens «Behandle avvik»-lenke peker hit (AVVIK_URL i
          server/morning-digest.ts). Uten ruta ga lenken 404. */}
      <Route path="/avvik">
        <ProtectedRoute>
          <AdminLayout>
            <IssuesPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/packing">
        <ProtectedRoute>
          <AdminLayout>
            <WarehousePacking />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/receiving">
        <ProtectedRoute>
          <AdminLayout>
            <WarehouseReceiving />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/inspection">
        <ProtectedRoute>
          <AdminLayout>
            <WarehouseInspection />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/preparation">
        <ProtectedRoute>
          <AdminLayout>
            <PreparationPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/statistics">
        <ProtectedRoute>
          <AdminLayout>
            <ReceivingStatistics />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/my-statistics">
        <ProtectedRoute>
          <AdminLayout>
            <MyStatisticsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/work-log">
        <ProtectedRoute>
          <AdminLayout>
            <WorkLogPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/my-packed-orders">
        <ProtectedRoute>
          <AdminLayout>
            <MyPackedOrdersPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/transport/shipping">
        <ProtectedRoute>
          <AdminLayout>
            <TransportShipping />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/transport/returns">
        <ProtectedRoute>
          <AdminLayout>
            <WarehouseReturnTracking />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/audit-log">
        <ProtectedRoute>
          <AdminLayout>
            <AuditLogPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/customers">
        <ProtectedRoute>
          <AdminLayout>
            <CustomersPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/products">
        <ProtectedRoute>
          <AdminLayout>
            <ProductsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/services">
        <ProtectedRoute>
          <AdminLayout>
            <ServicesPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/quotes">
        <ProtectedRoute>
          <AdminLayout>
            <QuotesPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/inquiries">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <InquiriesPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/quotes/new">
        <ProtectedRoute>
          <AdminLayout>
            <QuoteFormPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/quotes/:id/edit">
        <ProtectedRoute>
          <AdminLayout>
            <QuoteFormPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/quotes/:id">
        <ProtectedRoute>
          <AdminLayout>
            <QuoteDetailPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/contracts">
        <ProtectedRoute>
          <AdminLayout>
            <ContractsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/contracts/:id">
        <ProtectedRoute>
          <AdminLayout>
            <ContractDetailPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/contract-terms">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <ContractTermsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/settings">
        <ProtectedRoute>
          <AdminLayout>
            <SettingsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/users">
        <ProtectedRoute>
          <AdminLayout>
            <AdminUsersPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/notifications">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <AdminNotificationsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/warehouses">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <AdminWarehousesPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/inventory">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <AdminInventoryPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/file-archive">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <FileArchivePage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/shipping-options">
        <ProtectedRoute>
          <AdminLayout>
            <ShippingOptionsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/error-logs">
        <ProtectedRoute>
          <AdminLayout>
            <ErrorLogsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/email-inbox">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <AdminEmailInboxPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin/standard-packages">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <StandardPackagesPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      {/* Kapasitetskalender (intern): samme side som /warehouse/availability-calendar */}
      <Route path="/kapasitet">
        <ProtectedRoute>
          <AdminLayout>
            <AvailabilityCalendarPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      {/* Noekkeltall (kun admin-rolle og opp) */}
      <Route path="/nokkeltall">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <NokkeltallPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      {/* Oekonomi (kun admin-rolle og opp, som endepunktene i
          server/economy-routes.ts). Sidene er read-only. */}
      <Route path="/okonomi/fakturaer">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <OkonomiFakturaerPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/okonomi/kvitteringer">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <OkonomiKvitteringerPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      {/* Automatikk (kun admin-rolle og opp) */}
      <Route path="/automatikk">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <AutomationStatusPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      {/* Drift: alt som staar fast, paa ett sted (kun admin-rolle og opp) */}
      <Route path="/drift">
        <ProtectedRoute requiredRole="admin">
          <AdminLayout>
            <DriftPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/statistics">
        <ProtectedRoute>
          <AdminLayout>
            <UserStatisticsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/stock-status">
        <ProtectedRoute>
          <AdminLayout>
            <StockStatusPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      {/* Avspillingsenhet-oversikt: tech_crew-rolle og opp, som endepunktet (403 under level 3). */}
      <Route path="/warehouse/playback-devices">
        <ProtectedRoute requiredRole="tech_crew">
          <AdminLayout>
            <PlaybackDevicesPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      {/* Utlaan til samarbeidspartnere: alle innloggede ser lista, lager+ registrerer. */}
      <Route path="/loans">
        <ProtectedRoute>
          <AdminLayout>
            <LoansPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/availability-calendar">
        <ProtectedRoute>
          <AdminLayout>
            <AvailabilityCalendarPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/inventory-counts">
        <ProtectedRoute>
          <AdminLayout>
            <InventoryCountsPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/warehouse/discrepancy-log">
        <ProtectedRoute>
          <AdminLayout>
            <DiscrepancyLogPage />
          </AdminLayout>
        </ProtectedRoute>
      </Route>
      <Route path="/admin-signature-capture">
        <ProtectedRoute requiredRole="admin">
          <AdminSignatureCapturePage />
        </ProtectedRoute>
      </Route>
      
      {/* Fallback to 404 */}
      <Route component={NotFound} />
    </Switch>
  );
}

function SessionHandlerSetup() {
  const { showSessionExpired } = useSession();
  
  useEffect(() => {
    setSessionExpiredHandler(showSessionExpired);
    return () => setSessionExpiredHandler(null);
  }, [showSessionExpired]);
  
  return null;
}

function App() {
  useEffect(() => {
    initGlobalErrorHandlers();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <ThemeProvider>
            <TooltipProvider>
              <SessionProvider>
                <SessionHandlerSetup />
                <AuthProvider>
                  <WarehouseFilterProvider>
                    <Toaster />
                    <UpdateBanner />
                    {/* Inner boundary: a DOM-sync desync (browser translate / portal
                        teardown) recovers at route scope, keeping providers, auth and
                        query cache alive instead of remounting the whole app. */}
                    <ErrorBoundary>
                      <Suspense fallback={<RouteLoadingFallback />}>
                        <Router />
                      </Suspense>
                    </ErrorBoundary>
                  </WarehouseFilterProvider>
                </AuthProvider>
              </SessionProvider>
            </TooltipProvider>
          </ThemeProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
